import React, { useState, useMemo, useEffect } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";
import { Search, X, Loader2 } from "lucide-react";
import { getDocumentShares, removeDocumentShare } from "../utils/Api";
import { useSendEmail } from "../hooks/useSendEmail";
import { useAuth } from "../utils/AuthContext";
import { documentShareNotificationEmail } from "../emails";

interface Member {
  id: string;
  user_id: string | null;
  db_user_id: string | null;
  email: string;
  name?: string;
  role: string;
  status: string;
}

interface DocumentShare {
  id: string;
  document_id: string;
  shared_with_user_id: string;
  shared_with_email: string;
  shared_with_name?: string;
  shared_by_user_id?: string;
  created_at: string;
}

interface ShareDocumentModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  document: Record<string, any> | null;
  members: Member[];
  onShare: (userIds: string[]) => Promise<void>;
}

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  isOpen,
  setIsOpen,
  document,
  members,
  onShare,
}) => {
  const { user } = useAuth();
  const { sendEmail } = useSendEmail();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [existingShares, setExistingShares] = useState<DocumentShare[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchExistingShares = async () => {
      if (!document?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await getDocumentShares(document.id);
        if (!cancelled) {
          setExistingShares(response.data.shares || []);
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err.response?.status !== 403) {
            setError("Failed to load existing shares");
          }
          setExistingShares([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    if (isOpen && document?.id) {
      fetchExistingShares();
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, document?.id]);

  const fetchExistingShares = async () => {
    if (!document?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getDocumentShares(document.id);
      setExistingShares(response.data.shares || []);
    } catch (err: any) {
      if (err.response?.status !== 403) {
        setError("Failed to load existing shares");
      }
      setExistingShares([]);
    } finally {
      setIsLoading(false);
    }
  };

  const activeMembersOnly = members.filter(
    (m) => m.status !== "invited" && m.db_user_id,
  );

  const sharedUserIds = new Set(existingShares.map((s) => s.shared_with_user_id));
  const availableMembers = activeMembersOnly.filter(
    (m) =>
      m.db_user_id &&
      !sharedUserIds.has(m.db_user_id) &&
      m.user_id !== document?.user_id &&
      m.role.toLowerCase() !== "admin",
  );

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return availableMembers;
    const query = searchQuery.toLowerCase();
    return availableMembers.filter(
      (member) =>
        (member.name?.toLowerCase() || "").includes(query) ||
        member.email.toLowerCase().includes(query),
    );
  }, [availableMembers, searchQuery]);

  const toggleMemberSelection = (memberId: string) => {
    const newSelected = new Set(selectedMemberIds);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMemberIds(newSelected);
  };

  const handleShare = async () => {
    const selectedMembersList = Array.from(selectedMemberIds)
      .map((memberId) => members.find((m) => m.id === memberId))
      .filter((m): m is Member => m !== undefined);

    const userIds = selectedMembersList
      .map((member) => member.db_user_id)
      .filter((id): id is string => id !== null && id !== undefined);

    if (userIds.length === 0) return;

    setIsSharing(true);
    setError(null);
    try {
      await onShare(userIds);

      const documentUrl = `${window.location.origin}/view/${document?.id}`;
      const sharedByName = user?.name || user?.displayName || user?.email || "Someone";
      const documentName = document?.name || "Untitled Document";

      await Promise.all(
        selectedMembersList.map((member) =>
          sendEmail({
            to: member.email,
            template: documentShareNotificationEmail({
              documentName,
              sharedByName,
              documentUrl,
            }),
          }),
        ),
      );

      setSelectedMemberIds(new Set());
      await fetchExistingShares();
    } catch (err: any) {
      setError(err.message || "Failed to share document");
    } finally {
      setIsSharing(false);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    if (!document?.id) return;
    setError(null);
    try {
      await removeDocumentShare(document.id, shareId);
      setExistingShares((prev) => prev.filter((s) => s.id !== shareId));
    } catch (err: any) {
      setError(err.message || "Failed to remove share");
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setSelectedMemberIds(new Set());
    setExistingShares([]);
    setError(null);
    setIsOpen(false);
  };

  const selectedMembers = useMemo(() => {
    return Array.from(selectedMemberIds)
      .map((id) => members.find((m) => m.id === id))
      .filter((m): m is Member => m !== undefined);
  }, [selectedMemberIds, members]);

  if (!document) return null;

  return (
    <Modal isOpen={isOpen} setIsOpen={handleClose}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          Share document
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Share "{document.name || "Untitled Document"}" with organisation members.
        </p>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Left Column */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-2">
                Organisation members
              </p>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                />
              </div>

              <div className="h-48 sm:h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {availableMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400 p-4 text-center">
                    {existingShares.length > 0
                      ? "All members already have access."
                      : "No members available to share with."}
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400 p-4 text-center">
                    No members found matching "{searchQuery}"
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredMembers.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => toggleMemberSelection(member.id)}
                        className={`w-full flex items-center px-3 py-2.5 transition-colors text-left ${
                          selectedMemberIds.has(member.id)
                            ? "bg-[#1d365d]/5"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex-shrink-0 h-7 w-7 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-gray-600">
                            {(member.name || member.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.name || "—"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{member.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-2">
                Shared with ({existingShares.length + selectedMembers.length})
              </p>

              <div className="h-48 sm:h-[calc(64px+12px+256px)] overflow-y-auto border border-gray-200 rounded-lg">
                {existingShares.length === 0 && selectedMembers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400 p-4 text-center">
                    Select members from the left to share this document
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {existingShares.map((share) => (
                      <div
                        key={share.id}
                        className="flex items-center justify-between px-3 py-2.5 bg-green-50"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 h-7 w-7 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-xs font-medium text-green-700">
                              {(share.shared_with_name || share.shared_with_email)
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {share.shared_with_name || share.shared_with_email}
                            </p>
                            {share.shared_with_name && (
                              <p className="text-xs text-gray-500 truncate">
                                {share.shared_with_email}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveShare(share.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove access"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    {selectedMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between px-3 py-2.5 bg-[#1d365d]/5"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 h-7 w-7 bg-[#1d365d]/10 rounded-full flex items-center justify-center mr-3">
                            <span className="text-xs font-medium text-[#1d365d]">
                              {(member.name || member.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {member.name || "—"}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{member.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleMemberSelection(member.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-4 mt-2">
          <PrimaryButton
            onClick={handleShare}
            disabled={selectedMemberIds.size === 0 || isSharing}
            className="flex-1"
          >
            {isSharing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sharing...
              </span>
            ) : (
              `Share${selectedMemberIds.size > 0 ? ` (${selectedMemberIds.size})` : ""}`
            )}
          </PrimaryButton>
          <SecondaryButton onClick={handleClose} className="flex-1">
            Close
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default ShareDocumentModal;
